export type EquipmentType = 'MANUFACTURING' | 'INSPECTION';

export interface ManufacturingEquipment {
  equipmentId: number;
  sortOrder?: number;
  equipmentCode?: string | null;
  equipmentName: string;
  manufacturer?: string | null;
  installDate?: string | null;
  managerName?: string | null;
  performance?: string | null;
  powerKw?: string | null;
  voltage?: string | null;
  location?: string | null;
  equipmentType?: EquipmentType | string;
  ownershipType?: 'OWNED' | 'RENTAL' | string;
  rentalVendor?: string | null;
  rentalStartDate?: string | null;
  rentalEndDate?: string | null;
  calibCycleMonths?: number | null;
  calibWarnDays?: number | null;
  latestCalibValidDate?: string | null;
  calibStatus?: 'OK' | 'EXPIRING' | 'EXPIRED' | 'NONE' | string;
  useYn?: string;
  memo?: string | null;
  hasImage?: boolean;
  imageName?: string | null;
  deactivatedUserId?: number | null;
  deactivatedUserName?: string | null;
  deactivatedAt?: string | null;
  deactivateReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ManufacturingEquipmentPayload {
  equipmentCode?: string;
  equipmentName: string;
  manufacturer?: string;
  installDate?: string | null;
  managerName?: string;
  performance?: string;
  powerKw?: string;
  voltage?: string;
  location?: string;
  equipmentType?: EquipmentType | string;
  ownershipType?: 'OWNED' | 'RENTAL' | string;
  rentalVendor?: string | null;
  rentalStartDate?: string | null;
  rentalEndDate?: string | null;
  calibCycleMonths?: number | null;
  calibWarnDays?: number | null;
  useYn?: string;
  memo?: string;
}

export function isEquipmentInUse(useYn?: string | null) {
  return useYn !== 'N';
}

export function formatEquipmentUseYn(useYn?: string | null) {
  return isEquipmentInUse(useYn) ? '사용' : '미사용';
}

export interface EquipmentMainPart {
  partId: number;
  equipmentId: number;
  stockPartId?: number | null;
  sortOrder?: number;
  partName: string;
  spec?: string | null;
  qty?: number | null;
  remark?: string | null;
  stockQty?: number | null;
}

export interface EquipmentMainPartPayload {
  stockPartId?: number | null;
  partName?: string;
  spec?: string;
  qty?: number;
  remark?: string;
  initialStockQty?: number;
}

export interface EquipmentCalibrationAttachment {
  attachmentId: number;
  calibrationId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface EquipmentCalibration {
  calibrationId: number;
  equipmentId: number;
  calibDate?: string | null;
  agency?: string | null;
  calibNo?: string | null;
  validDate?: string | null;
  remark?: string | null;
  status?: 'OK' | 'EXPIRING' | 'EXPIRED' | 'NONE' | string;
  expiringSoon?: boolean;
  expired?: boolean;
  attachments?: EquipmentCalibrationAttachment[];
}

export interface EquipmentCalibrationPayload {
  calibDate?: string | null;
  agency?: string;
  calibNo?: string;
  validDate?: string | null;
  remark?: string;
}

export interface EquipmentRepair {
  repairId: number;
  equipmentId: number;
  repairDate?: string | null;
  partArea?: string | null;
  details?: string | null;
  cost?: number | null;
  vendor?: string | null;
  stockPartId?: number | null;
  stockPartName?: string | null;
  usedQty?: number | null;
}

export interface EquipmentRepairPayload {
  repairDate?: string | null;
  partArea?: string;
  details?: string;
  cost?: number | null;
  vendor?: string;
  stockPartId?: number | null;
  usedQty?: number | null;
}

export interface ConnectedEquipment {
  equipmentId: number;
  equipmentName: string;
  equipmentCode?: string | null;
}

export interface ManufacturingStockPart {
  stockPartId: number;
  partName: string;
  spec?: string | null;
  stockQty: number;
  remark?: string | null;
  connectedEquipments?: ConnectedEquipment[];
}

export interface ManufacturingStockPartPayload {
  partName: string;
  spec?: string;
  stockQty?: number;
  remark?: string;
}

export type EquipmentDocType =
  | 'MANAGEMENT_STANDARD'
  | 'INSPECTION_SHEET'
  | 'OPERATION_STANDARD';

export interface EquipmentDocument {
  documentId: number;
  equipmentId: number;
  docType: EquipmentDocType | string;
  title?: string | null;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
  memo?: string | null;
  createdAt?: string;
}

export interface InspectionCriteriaRow {
  criteriaId?: number;
  sortOrder?: number;
  inspectionPoint?: string;
  inspectionItem?: string;
  cycleCode?: string;
  timingCode?: string;
  method?: string;
  criteria?: string;
  actionOnAbnormal?: string;
}

export interface LubricationCriteriaRow {
  lubricationId?: number;
  sortOrder?: number;
  lubricationPoint?: string;
  lubricant?: string;
  cycleCode?: string;
  amount?: string;
  method?: string;
  wasteDisposal?: string;
}

export interface EquipmentManagementStandard {
  standardId?: number;
  equipmentId: number;
  equipmentCode?: string | null;
  equipmentName?: string | null;
  hasEquipmentImage?: boolean;
  safetyNotes?: string | null;
  mainCheckItems?: string | null;
  handoverNotes?: string | null;
  operationStandard?: string | null;
  createdUserId?: number | null;
  createdUserName?: string | null;
  writtenDate?: string | null;
  updatedUserId?: number | null;
  updatedUserName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  approvedUserId?: number | null;
  approvedUserName?: string | null;
  approvedAt?: string | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  inspections: InspectionCriteriaRow[];
  lubrications: LubricationCriteriaRow[];
}

export interface EquipmentManagementStandardPayload {
  safetyNotes?: string;
  mainCheckItems?: string;
  handoverNotes?: string;
  operationStandard?: string;
  writtenDate?: string | null;
  inspections: InspectionCriteriaRow[];
  lubrications: LubricationCriteriaRow[];
}

export interface InspectionSheetLine {
  lineId: number;
  sortOrder?: number;
  inspectionPoint?: string | null;
  inspectionItem?: string | null;
  cycleCode?: string | null;
}

export interface InspectionSheetMark {
  lineId: number;
  halfNo: number;
  monthIndex: number;
  weekNo: number;
  markValue?: string | null;
}

export interface InspectionSheetDateCell {
  halfNo: number;
  monthIndex: number;
  weekNo: number;
  inspectionDate?: string | null;
}

export interface InspectionSheetAction {
  actionId?: number;
  actionDate?: string | null;
  abnormality?: string;
  repairDetail?: string;
  remark?: string;
}

export interface EquipmentInspectionSheet {
  sheetId: number;
  equipmentId: number;
  equipmentCode?: string | null;
  equipmentName?: string | null;
  sheetYear: number;
  status: 'DRAFT' | 'CONFIRMED' | string;
  createdUserId?: number | null;
  createdUserName?: string | null;
  updatedUserId?: number | null;
  updatedUserName?: string | null;
  confirmedUserId?: number | null;
  confirmedUserName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  confirmedAt?: string | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  lines: InspectionSheetLine[];
  marks: InspectionSheetMark[];
  dateCells: InspectionSheetDateCell[];
  actions: InspectionSheetAction[];
}

export interface EquipmentInspectionSheetPayload {
  marks: InspectionSheetMark[];
  dateCells: InspectionSheetDateCell[];
  actions: InspectionSheetAction[];
}
